import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import ConfirmButton from '../../components/common/ConfirmButton';
import Header from '../../components/common/Header';
import { Context } from '../../context/Context';
import HeliosClient from '../../context/Helios';
import { ActionType, AppContext, DispatchFunction, Brands, IndividualStateKey } from '../../context/interfaces';
import ProgressBar from '../../components/ProgressBar';
import { completeSectionGTM, initializeSectionGTM } from '../../shared/utils/gooogleTagManager';
import { BRAND, urlIdRewrite } from 'shared/utils';
import '../../assets/styles/screens/insured/Narration.scss';

const MIN_NARRATION_LENGTH = 10;
const INPUT_NARRATION = 'narrationData';
const INPUT_ADITIONAL = 'aditionalData';

export const updateText = (
	event: React.ChangeEvent<HTMLTextAreaElement>,
	dispatch: DispatchFunction,
	stateKey: IndividualStateKey
): void => {
	if (event.target.id === INPUT_NARRATION) {
		dispatch({ type: ActionType.SET_NARRATION_TEXT, data: { value: event.target.value, key: stateKey } });
	} else if (event.target.id === INPUT_ADITIONAL) {
		dispatch({ type: ActionType.SET_NARRATION_ADITIONAL_TEXT, data: { value: event.target.value, key: stateKey } });
	}
};

interface Props {
	to: string;
	stateKey: IndividualStateKey;
	screenKey: any;
}

const StoryScreen = (props: Props): JSX.Element => {
	const context = useContext(Context) as AppContext;
	const locationPath = useLocation();
	const { dispatch, insured, injured, location } = context;
	const { id } = useParams<{ id: string }>();
	const [nextScreen, setNextScreen] = useState(urlIdRewrite(props.to, id));
	const [initLoadGT, setInitLoadGT] = useState(true);
	const setText = (event: React.ChangeEvent<HTMLTextAreaElement>): void => updateText(event, dispatch, props.stateKey);
	const narrationData = props.stateKey === IndividualStateKey.INSURED ? insured.narration : injured.narration;
	const aditionalData = insured?.narrationAdditional || '';
	// Para Qualitas/INS el relato viene en narration (EventStory/narracionHechos); mostrarlo y validar con ese campo
	const isQualitasOrIns = BRAND === Brands.QUALITAS || BRAND === Brands.INS;
	const canContinue = isQualitasOrIns
		? narrationData?.trim().length > MIN_NARRATION_LENGTH
		: aditionalData?.trim().length > MIN_NARRATION_LENGTH;
	const showStory = false;
	const [textData] = useState(narrationData);

	useEffect(() => {
		if (initLoadGT) {
			initializeSectionGTM(context, locationPath.pathname);
			setInitLoadGT(false);
		}
	}, [initLoadGT, context, locationPath.pathname]);

	useEffect(() => {
		if (
			[Brands.LAFISE, Brands.MNK, Brands.QUALITAS, Brands.PURDY, Brands.INS].includes(BRAND as Brands) &&
			location.selectedLocation === 'default'
		) {
			setNextScreen('/' + id + '/insured/damages');
		}
	}, [location, id]);

	const handleConfirm = (): void => {
		// Only Qualitas
		completeSectionGTM(context, locationPath.pathname);
		new HeliosClient().saveInspection(context, id);
	};

	const disabledNarration = (): boolean => {
		return (isCRAccount() && textData?.trim().length > 0) as boolean;
	};

	const isCRAccount = (): boolean => {
		return [Brands.ASSA, Brands.LAFISE, Brands.MNK, Brands.QUALITAS, Brands.PURDY, Brands.INS, Brands.IS_CR].includes(BRAND as Brands);
	};

	return (
		<div className="narration-screen">
			{props.screenKey && <Breadcrumbs currentStep={props.screenKey} />}
			<div className="container">
				<ProgressBar screenIndex={props.screenKey} />
				<Header title="Escriba una breve descripción del accidente" />
				<div className="body">
					{showStory && (
						<textarea
							id={INPUT_NARRATION}
							disabled={disabledNarration()}
							placeholder="Describa los hechos"
							rows={5}
							value={narrationData}
							onChange={setText}
							required={true}
						/>
					)}
					{/* Qualitas/INS: mostrar y editar el relato principal (narration = EventStory/narracionHechos) como en ASSA */}
					{isQualitasOrIns ? (
						<textarea
							id={INPUT_NARRATION}
							placeholder="Describa los hechos"
							rows={5}
							value={narrationData}
							onChange={setText}
							required={true}
						/>
					) : (
						<textarea id={INPUT_ADITIONAL} placeholder="Relato" rows={5} value={aditionalData} onChange={setText} />
					)}
					<ConfirmButton to={nextScreen} disabled={!canContinue} onClick={handleConfirm} />
				</div>
			</div>
		</div>
	);
};

export default StoryScreen;
